// Financial-statement style PDF generators for Monthly and Annual P&L.
//
// Per-Event / Monthly / Annual reports use the same general visual system:
// - Helvetica typography
// - Black / grey / white palette
// - Centered GAO CHILL title block
// - Financial statement style main P&L
// - Supporting schedules
// - Footer with Internal Use and page numbers
//
// Calculations are NOT performed here.
// The calling page supplies all calculated figures.

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDate } from "@/lib/formatters";

// ============================================================
// SHARED HELPERS
// ============================================================

const fmt = (value) => {
  const num =
    parseFloat(value) || 0;

  const abs =
    Math.abs(num).toLocaleString(
      "en-MY",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );

  return num < 0
    ? `(${abs})`
    : abs;
};

const fmtPct = (value) => {
  const num =
    parseFloat(value) || 0;

  const abs =
    Math.abs(num).toFixed(2);

  return num < 0
    ? `(${abs}%)`
    : `${abs}%`;
};

const sanitize = (s) =>
  String(s || "report")
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// ============================================================
// STYLES
// ============================================================

const HEAD_STYLE_MAIN = {
  fontStyle: "bold",
  fillColor: [235, 235, 235],
  textColor: [0, 0, 0],
  lineWidth: {
    top: 0.5,
    bottom: 0.5,
  },
};

const HEAD_STYLE_SCHEDULE = {
  fontStyle: "bold",
  fillColor: [235, 235, 235],
  textColor: [0, 0, 0],
};

const CELL_STYLES_MAIN = {
  font: "helvetica",
  fontSize: 10,
  textColor: [30, 30, 30],
  cellPadding: {
    top: 3,
    bottom: 3,
    left: 4,
    right: 4,
  },
  lineColor: [80, 80, 80],
};

const CELL_STYLES_SCHEDULE = {
  font: "helvetica",
  fontSize: 9,
  textColor: [30, 30, 30],
  cellPadding: 3,
  lineColor: [150, 150, 150],
};

const boldTop = (content) => ({
  content,
  styles: {
    fontStyle: "bold",
    lineWidth: {
      top: 0.5,
    },
  },
});

const boldCell = (content) => ({
  content,
  styles: {
    fontStyle: "bold",
  },
});

// ============================================================
// COMMON PDF LAYOUT HELPERS
// ============================================================

const drawChromeOnAllPages = (
  doc,
  {
    runningHeaderText,
    footerCenterText,
    margin,
  }
) => {
  const totalPages =
    doc.getNumberOfPages();

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const pageHeight =
    doc.internal.pageSize.getHeight();

  for (
    let i = 1;
    i <= totalPages;
    i++
  ) {
    doc.setPage(i);

    doc.setFont(
      "helvetica",
      "normal"
    );

    doc.setFontSize(8);

    doc.setTextColor(
      90,
      90,
      90
    );

    if (
      i > 1 &&
      runningHeaderText
    ) {
      doc.text(
        runningHeaderText,
        margin,
        margin - 15
      );

      doc.setLineWidth(0.2);

      doc.line(
        margin,
        margin - 10,
        pageWidth - margin,
        margin - 10
      );
    }

    doc.setLineWidth(0.2);

    doc.line(
      margin,
      pageHeight - 30,
      pageWidth - margin,
      pageHeight - 30
    );

    doc.text(
      "Internal Use",
      margin,
      pageHeight - 18
    );

    if (footerCenterText) {
      doc.text(
        footerCenterText,
        pageWidth / 2,
        pageHeight - 18,
        {
          align: "center",
        }
      );
    }

    doc.text(
      `Page ${i} of ${totalPages}`,
      pageWidth - margin,
      pageHeight - 18,
      {
        align: "right",
      }
    );

    doc.setTextColor(
      0,
      0,
      0
    );
  }
};

const drawTitleBlock = (
  doc,
  {
    title,
    margin,
  }
) => {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(14);

  doc.text(
    "GAO CHILL",
    pageWidth / 2,
    margin + 6,
    {
      align: "center",
    }
  );

  doc.setFont(
    "helvetica",
    "normal"
  );

  doc.setFontSize(9);

  doc.text(
    "Herbal Beverage Business",
    pageWidth / 2,
    margin + 22,
    {
      align: "center",
    }
  );

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(13);

  doc.text(
    title,
    pageWidth / 2,
    margin + 48,
    {
      align: "center",
    }
  );

  doc.setLineWidth(0.8);

  doc.line(
    margin,
    margin + 58,
    pageWidth - margin,
    margin + 58
  );

  return margin + 58;
};

const drawInfoBlock = (
  doc,
  {
    lines,
    startY,
    margin,
  }
) => {
  doc.setFontSize(10);

  let y =
    startY;

  const lineHeight =
    14;

  lines.forEach(
    ([label, value]) => {
      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.text(
        label,
        margin,
        y
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.text(
        String(value ?? ""),
        margin + 130,
        y
      );

      y += lineHeight;
    }
  );

  return y;
};

const startSchedule = (
  doc,
  {
    title,
    margin,
    cursorY,
    minRoomBelow = 140,
  }
) => {
  const pageHeight =
    doc.internal.pageSize.getHeight();

  const pageWidth =
    doc.internal.pageSize.getWidth();

  let y =
    cursorY;

  if (
    y + minRoomBelow >
    pageHeight - 40
  ) {
    doc.addPage();
    y = margin + 30;
  }

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(12);

  doc.setTextColor(
    0,
    0,
    0
  );

  doc.text(
    title,
    margin,
    y
  );

  doc.setLineWidth(0.4);

  doc.line(
    margin,
    y + 6,
    pageWidth - margin,
    y + 6
  );

  return y + 14;
};

const drawNetProfitDoubleRule = (
  doc,
  {
    margin,
    y,
  }
) => {
  const pageWidth =
    doc.internal.pageSize.getWidth();

  doc.setLineWidth(0.5);

  doc.line(
    pageWidth - margin - 130,
    y + 2,
    pageWidth - margin,
    y + 2
  );

  doc.line(
    pageWidth - margin - 130,
    y + 5,
    pageWidth - margin,
    y + 5
  );
};

// ============================================================
// MONTHLY P&L
// ============================================================

export const generateMonthlyPLPDF = ({
  monthLabel,
  periodStart,
  periodEnd,

  totalRevenue,
  totalProductCost,

  grossProfit,
  grossMargin,

  eventExpenses,

  companyOpex,

  netProfit,
  netMargin,

  ownerDraws,
  profitRetained,

  cashSummary,

  activities = [],

  eventExpenseBreakdown = [],

  productPurchaseTransactions = [],

  companyOpexTransactions = [],
}) => {
  const doc =
    new jsPDF({
      unit: "pt",
      format: "a4",
    });

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const margin =
    40;

  const contentWidth =
    pageWidth - 2 * margin;

  const reportGeneratedDate =
    formatDate(
      new Date()
        .toISOString()
        .split("T")[0]
    );

  // ============================================================
  // PAGE 1 — MONTHLY STATEMENT
  // ============================================================

  drawTitleBlock(
    doc,
    {
      title:
        "MONTHLY MANAGEMENT PROFIT & LOSS STATEMENT",

      margin,
    }
  );

  const infoStartY =
    drawInfoBlock(
      doc,
      {
        margin,

        startY:
          margin + 78,

        lines: [
          [
            "Reporting Month:",
            monthLabel,
          ],

          [
            "Reporting Period:",
            `${formatDate(
              periodStart
            )} to ${formatDate(
              periodEnd
            )}`,
          ],

          [
            "Currency:",
            "Malaysian Ringgit (RM)",
          ],

          [
            "Report Generated:",
            reportGeneratedDate,
          ],
        ],
      }
    );

  const body = [];

  // REVENUE
  body.push([
    boldCell("REVENUE"),
    "",
  ]);

  body.push([
    "    Event / Activity Revenue",
    fmt(totalRevenue),
  ]);

  body.push([
    boldTop("TOTAL REVENUE"),
    boldTop(
      fmt(totalRevenue)
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  // PRODUCT / STOCK PURCHASES
  body.push([
    boldCell(
      "LESS: PRODUCT / STOCK PURCHASES"
    ),
    "",
  ]);

  body.push([
    "    Ingredients, Cups, Packaging & Other Product Purchases",
    fmt(totalProductCost),
  ]);

  body.push([
    boldTop(
      "TOTAL PRODUCT / STOCK PURCHASES"
    ),
    boldTop(
      fmt(totalProductCost)
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  // GROSS PROFIT
  body.push([
    boldCell("GROSS PROFIT"),
    boldCell(
      fmt(grossProfit)
    ),
  ]);

  body.push([
    "    Gross Profit Margin",
    fmtPct(grossMargin),
  ]);

  body.push([
    "",
    "",
  ]);

  // EVENT EXPENSES
  body.push([
    boldCell(
      "LESS: EVENT / ACTIVITY OPERATING EXPENSES"
    ),
    "",
  ]);

  body.push([
    "    Event Rental",
    fmt(
      eventExpenses.rental
    ),
  ]);

  body.push([
    "    Staff Wages / Event Manpower",
    fmt(
      eventExpenses.staff
    ),
  ]);

  body.push([
    "    Commission",
    fmt(
      eventExpenses.commission ||
        0
    ),
  ]);

  body.push([
    "    Transport Expense",
    fmt(
      eventExpenses.transport
    ),
  ]);

  body.push([
    "    Event Marketing",
    fmt(
      eventExpenses.marketing ||
        0
    ),
  ]);

  body.push([
    "    Other Event Expenses",
    fmt(
      eventExpenses.other
    ),
  ]);

  body.push([
    boldTop(
      "TOTAL EVENT / ACTIVITY EXPENSES"
    ),
    boldTop(
      fmt(
        eventExpenses.total
      )
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  // COMPANY OPERATING EXPENSES
  body.push([
    boldCell(
      "LESS: COMPANY OPERATING EXPENSES"
    ),
    "",
  ]);

  if (
    companyOpex.items.length ===
    0
  ) {
    body.push([
      "    (None recorded)",
      fmt(0),
    ]);
  } else {
    companyOpex.items.forEach(
      (it) => {
        body.push([
          `    ${it.name}`,
          fmt(it.amount),
        ]);
      }
    );
  }

  body.push([
    boldTop(
      "TOTAL COMPANY OPERATING EXPENSES"
    ),
    boldTop(
      fmt(
        companyOpex.total
      )
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  // NET PROFIT
  body.push([
    {
      content:
        "NET PROFIT / (LOSS)",

      styles: {
        fontStyle: "bold",

        fillColor: [
          240,
          240,
          240,
        ],

        lineWidth: {
          top: 0.8,
          bottom: 0.5,
        },
      },
    },

    {
      content:
        fmt(netProfit),

      styles: {
        fontStyle: "bold",

        fillColor: [
          240,
          240,
          240,
        ],

        lineWidth: {
          top: 0.8,
          bottom: 0.5,
        },
      },
    },
  ]);

  body.push([
    boldCell(
      "Net Profit Margin"
    ),
    boldCell(
      fmtPct(netMargin)
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        infoStartY + 12,

      head: [
        [
          {
            content:
              "Description",

            styles: {
              halign: "left",
            },
          },

          {
            content:
              "RM",

            styles: {
              halign: "right",
            },
          },
        ],
      ],

      body,

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      headStyles:
        HEAD_STYLE_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            130,
        },

        1: {
          halign:
            "right",

          cellWidth:
            130,
        },
      },

      margin: {
        left: margin,
        right: margin,
      },
    }
  );

  drawNetProfitDoubleRule(
    doc,
    {
      margin,

      y:
        doc.lastAutoTable
          .finalY,
    }
  );

  const disclaimerY =
    doc.lastAutoTable
      .finalY + 26;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  const disclaimerLines =
    doc.splitTextToSize(
      "This is an internal monthly management report prepared from records in the Gao Chill bookkeeping system. Supporting invoices, receipts, payment records, and bank records should be retained separately for accounting and tax purposes.",

      contentWidth
    );

  doc.text(
    disclaimerLines,
    margin,
    disclaimerY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  // ============================================================
  // SCHEDULE A — ACTIVITIES
  // ============================================================

  doc.addPage();

  let cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE A — ACTIVITIES INCLUDED",

        margin,

        cursorY:
          margin + 30,
      }
    );

  if (
    activities.length ===
    0
  ) {
    doc.setFont(
      "helvetica",
      "italic"
    );

    doc.setFontSize(10);

    doc.text(
      "No completed activities in this reporting month.",

      margin,

      cursorY + 6
    );

    cursorY += 20;
  } else {
    const rows =
      activities.map(
        (a) => [
          a.name,

          a.activityType ||
            "—",

          fmt(
            a.revenue
          ),

          fmt(
            a.productCost
          ),

          fmt(
            a.eventExpenses
          ),

          fmt(
            a.netProfit
          ),

          fmtPct(
            a.netMargin
          ),
        ]
      );

    const totalsA =
      activities.reduce(
        (
          acc,
          a
        ) => ({
          revenue:
            acc.revenue +
            (a.revenue ||
              0),

          productCost:
            acc.productCost +
            (a.productCost ||
              0),

          eventExpenses:
            acc.eventExpenses +
            (a.eventExpenses ||
              0),

          netProfit:
            acc.netProfit +
            (a.netProfit ||
              0),
        }),

        {
          revenue: 0,
          productCost: 0,
          eventExpenses: 0,
          netProfit: 0,
        }
      );

    const totalMargin =
      totalsA.revenue >
      0
        ? (
            totalsA.netProfit /
            totalsA.revenue
          ) * 100
        : 0;

    rows.push([
      {
        content:
          "TOTAL",

        colSpan: 2,

        styles: {
          fontStyle:
            "bold",
        },
      },

      boldCell(
        fmt(
          totalsA.revenue
        )
      ),

      boldCell(
        fmt(
          totalsA.productCost
        )
      ),

      boldCell(
        fmt(
          totalsA.eventExpenses
        )
      ),

      boldCell(
        fmt(
          totalsA.netProfit
        )
      ),

      boldCell(
        fmtPct(
          totalMargin
        )
      ),
    ]);

    autoTable(
      doc,
      {
        startY:
          cursorY,

        head: [
          [
            "Activity Name",
            "Activity Type",
            "Revenue RM",
            "Product Cost RM",
            "Event Expenses RM",
            "Net Profit RM",
            "Net Margin",
          ],
        ],

        body:
          rows,

        theme:
          "grid",

        styles:
          CELL_STYLES_SCHEDULE,

        headStyles:
          HEAD_STYLE_SCHEDULE,

        columnStyles: {
          0: {
            halign:
              "left",
          },

          1: {
            halign:
              "left",
          },

          2: {
            halign:
              "right",
          },

          3: {
            halign:
              "right",
          },

          4: {
            halign:
              "right",
          },

          5: {
            halign:
              "right",
          },

          6: {
            halign:
              "right",
          },
        },

        margin: {
          left:
            margin,

          right:
            margin,
        },
      }
    );

    cursorY =
      doc.lastAutoTable
        .finalY + 20;
  }

  // ============================================================
  // SCHEDULE B — EVENT EXPENSE BREAKDOWN
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE B — EVENT / ACTIVITY EXPENSE BREAKDOWN",

        margin,

        cursorY,
      }
    );

  const schBRows =
    eventExpenseBreakdown.map(
      (r) => [
        r.category,
        fmt(r.amount),
      ]
    );

  schBRows.push([
    boldCell("TOTAL"),

    boldCell(
      fmt(
        eventExpenses.total
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      head: [
        [
          "Expense Category",
          "Amount RM",
        ],
      ],

      body:
        schBRows,

      theme:
        "grid",

      styles:
        CELL_STYLES_SCHEDULE,

      headStyles:
        HEAD_STYLE_SCHEDULE,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 20;

  // ============================================================
  // SCHEDULE C — PRODUCT / STOCK PURCHASES
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE C — PRODUCT / STOCK PURCHASES",

        margin,

        cursorY,
      }
    );

  if (
    productPurchaseTransactions.length ===
    0
  ) {
    doc.setFont(
      "helvetica",
      "italic"
    );

    doc.setFontSize(10);

    doc.text(
      "No product / stock purchases recorded for this reporting month.",

      margin,

      cursorY + 6
    );

    cursorY += 20;
  } else {
    const rows =
      productPurchaseTransactions.map(
        (t) => [
          formatDate(
            t.date
          ),

          t.category ||
            "—",

          t.description ||
            "—",

          t.supplier ||
            "—",

          fmt(
            t.amount
          ),
        ]
      );

    rows.push([
      {
        content:
          "TOTAL",

        colSpan: 4,

        styles: {
          fontStyle:
            "bold",
        },
      },

      boldCell(
        fmt(
          totalProductCost
        )
      ),
    ]);

    autoTable(
      doc,
      {
        startY:
          cursorY,

        head: [
          [
            "Date",
            "Category",
            "Description",
            "Supplier",
            "Amount RM",
          ],
        ],

        body:
          rows,

        theme:
          "grid",

        styles:
          CELL_STYLES_SCHEDULE,

        headStyles:
          HEAD_STYLE_SCHEDULE,

        columnStyles: {
          0: {
            halign:
              "left",

            cellWidth:
              68,
          },

          1: {
            halign:
              "left",

            cellWidth:
              105,
          },

          2: {
            halign:
              "left",
          },

          3: {
            halign:
              "left",

            cellWidth:
              100,
          },

          4: {
            halign:
              "right",

            cellWidth:
              75,
          },
        },

        margin: {
          left:
            margin,

          right:
            margin,
        },
      }
    );

    cursorY =
      doc.lastAutoTable
        .finalY + 20;
  }

  // ============================================================
  // SCHEDULE D — COMPANY OPERATING EXPENSES
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE D — COMPANY OPERATING EXPENSES",

        margin,

        cursorY,
      }
    );

  if (
    companyOpexTransactions.length ===
    0
  ) {
    doc.setFont(
      "helvetica",
      "italic"
    );

    doc.setFontSize(10);

    doc.text(
      "No company operating expenses recorded for this reporting month.",

      margin,

      cursorY + 6
    );

    cursorY += 20;
  } else {
    const rows =
      companyOpexTransactions.map(
        (t) => [
          formatDate(
            t.date
          ),

          t.category ||
            "—",

          t.description ||
            "—",

          fmt(
            t.amount
          ),
        ]
      );

    rows.push([
      {
        content:
          "TOTAL",

        colSpan: 3,

        styles: {
          fontStyle:
            "bold",
        },
      },

      boldCell(
        fmt(
          companyOpex.total
        )
      ),
    ]);

    autoTable(
      doc,
      {
        startY:
          cursorY,

        head: [
          [
            "Date",
            "Category",
            "Description",
            "Amount RM",
          ],
        ],

        body:
          rows,

        theme:
          "grid",

        styles:
          CELL_STYLES_SCHEDULE,

        headStyles:
          HEAD_STYLE_SCHEDULE,

        columnStyles: {
          0: {
            halign:
              "left",

            cellWidth:
              70,
          },

          1: {
            halign:
              "left",

            cellWidth:
              130,
          },

          2: {
            halign:
              "left",
          },

          3: {
            halign:
              "right",

            cellWidth:
              90,
          },
        },

        margin: {
          left:
            margin,

          right:
            margin,
        },
      }
    );

    cursorY =
      doc.lastAutoTable
        .finalY + 20;
  }

  // ============================================================
  // SCHEDULE E — OWNER WITHDRAWALS
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE E — OWNER WITHDRAWALS AND PROFIT RETAINED",

        margin,

        cursorY,

        minRoomBelow:
          130,
      }
    );

  autoTable(
    doc,
    {
      startY:
        cursorY,

      body: [
        [
          "Monthly Net Profit",
          fmt(
            netProfit
          ),
        ],

        [
          "Less: Nelson Owner Withdrawals",
          fmt(
            ownerDraws
          ),
        ],

        [
          boldTop(
            "Profit Retained in Business"
          ),

          boldTop(
            fmt(
              profitRetained
            )
          ),
        ],
      ],

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 10;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  doc.text(
    "Owner withdrawals do not reduce business net profit.",

    margin,

    cursorY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  cursorY += 22;

  // ============================================================
  // SCHEDULE F — CASH SUMMARY
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE F — CASH SUMMARY",

        margin,

        cursorY,

        minRoomBelow:
          180,
      }
    );

  const cashRows = [
    [
      "Opening Cash Balance",

      fmt(
        cashSummary.openingCash ||
          0
      ),
    ],

    [
      "Cash Received",

      fmt(
        cashSummary.cashReceived
      ),
    ],

    [
      "Operating Expenses Paid",

      fmt(
        cashSummary.opexPaid
      ),
    ],

    [
      "Product Purchases Paid",

      fmt(
        cashSummary.productPurchases
      ),
    ],

    [
      "Asset Purchases Paid",

      fmt(
        cashSummary.assetPurchases
      ),
    ],

    [
      "Owner Withdrawals",

      fmt(
        cashSummary.ownerWithdrawals
      ),
    ],
  ];

  if (
    (
      cashSummary.pendingInflow ||
      0
    ) !== 0
  ) {
    cashRows.push([
      "Confirmed Pending Cash Inflows",

      fmt(
        cashSummary.pendingInflow
      ),
    ]);
  }

  if (
    (
      cashSummary.pendingOutflow ||
      0
    ) !== 0
  ) {
    cashRows.push([
      "Confirmed Pending Cash Outflows",

      fmt(
        cashSummary.pendingOutflow
      ),
    ]);
  }

  cashRows.push([
    boldTop(
      "Net Cash Movement"
    ),

    boldTop(
      fmt(
        cashSummary.netCashMovement
      )
    ),
  ]);

  cashRows.push([
    boldCell(
      "Closing Cash Balance"
    ),

    boldCell(
      fmt(
        cashSummary.closingCash
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      body:
        cashRows,

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 10;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  doc.text(
    "Cash Summary is separate from the Profit and Loss Statement.",

    margin,

    cursorY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  // Header / footer
  drawChromeOnAllPages(
    doc,
    {
      runningHeaderText:
        "Gao Chill — Monthly Management Accounts",

      footerCenterText:
        `Monthly P&L · ${monthLabel}`,

      margin,
    }
  );

  const filename =
    `Gao-Chill-Monthly-Management-P&L-${sanitize(
      monthLabel
    )}.pdf`;

  doc.save(
    filename
  );

  return filename;
};

// ============================================================
// ANNUAL P&L
// ============================================================
//
// Annual logic below is intentionally preserved.
// We are NOT changing the Annual P&L calculation in this update.

export const generateAnnualPLPDF = ({
  year,

  totalRevenue,

  totalProductCost,

  grossProfit,

  grossMargin,

  eventExpenses,

  companyOpex,

  netProfit,

  netMargin,

  ownerDraws,

  profitRetained,

  cashSummary,

  monthlyComparison = [],

  eventExpenseBreakdown = [],

  annualOpexBreakdown = [],
}) => {
  const doc =
    new jsPDF({
      unit: "pt",
      format: "a4",
    });

  const pageWidth =
    doc.internal.pageSize.getWidth();

  const margin =
    40;

  const contentWidth =
    pageWidth - 2 * margin;

  const reportGeneratedDate =
    formatDate(
      new Date()
        .toISOString()
        .split("T")[0]
    );

  // ============================================================
  // PAGE 1 — ANNUAL STATEMENT
  // ============================================================

  drawTitleBlock(
    doc,
    {
      title:
        "ANNUAL MANAGEMENT PROFIT & LOSS STATEMENT",

      margin,
    }
  );

  const infoStartY =
    drawInfoBlock(
      doc,
      {
        margin,

        startY:
          margin + 78,

        lines: [
          [
            "Financial Year:",
            String(year),
          ],

          [
            "Reporting Period:",
            `${formatDate(
              `${year}-01-01`
            )} to ${formatDate(
              `${year}-12-31`
            )}`,
          ],

          [
            "Currency:",
            "Malaysian Ringgit (RM)",
          ],

          [
            "Report Generated:",
            reportGeneratedDate,
          ],
        ],
      }
    );

  const body = [];

  body.push([
    boldCell("REVENUE"),
    "",
  ]);

  body.push([
    "    Event / Activity Revenue",
    fmt(totalRevenue),
  ]);

  body.push([
    boldTop("TOTAL REVENUE"),
    boldTop(
      fmt(totalRevenue)
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  body.push([
    boldCell(
      "LESS: COST OF GOODS SOLD"
    ),
    "",
  ]);

  body.push([
    "    Product Cost",
    fmt(totalProductCost),
  ]);

  body.push([
    boldTop(
      "TOTAL COST OF GOODS SOLD"
    ),
    boldTop(
      fmt(totalProductCost)
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  body.push([
    boldCell("GROSS PROFIT"),
    boldCell(
      fmt(grossProfit)
    ),
  ]);

  body.push([
    "    Gross Profit Margin",
    fmtPct(grossMargin),
  ]);

  body.push([
    "",
    "",
  ]);

  body.push([
    boldCell(
      "LESS: EVENT / ACTIVITY OPERATING EXPENSES"
    ),
    "",
  ]);

  body.push([
    "    Event Rental",
    fmt(
      eventExpenses.rental
    ),
  ]);

  body.push([
    "    Staff Wages / Event Manpower",
    fmt(
      eventExpenses.staff
    ),
  ]);

  body.push([
    "    Commission",
    fmt(
      eventExpenses.commission ||
        0
    ),
  ]);

  body.push([
    "    Transport Expense",
    fmt(
      eventExpenses.transport
    ),
  ]);

  body.push([
    "    Event Marketing",
    fmt(
      eventExpenses.marketing ||
        0
    ),
  ]);

  body.push([
    "    Other Event Expenses",
    fmt(
      eventExpenses.other
    ),
  ]);

  body.push([
    boldTop(
      "TOTAL EVENT / ACTIVITY EXPENSES"
    ),
    boldTop(
      fmt(
        eventExpenses.total
      )
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  body.push([
    boldCell(
      "LESS: COMPANY OPERATING EXPENSES"
    ),
    "",
  ]);

  if (
    companyOpex.items.length ===
    0
  ) {
    body.push([
      "    (None recorded)",
      fmt(0),
    ]);
  } else {
    companyOpex.items.forEach(
      (it) =>
        body.push([
          `    ${it.name}`,
          fmt(
            it.amount
          ),
        ])
    );
  }

  body.push([
    boldTop(
      "TOTAL COMPANY OPERATING EXPENSES"
    ),
    boldTop(
      fmt(
        companyOpex.total
      )
    ),
  ]);

  body.push([
    "",
    "",
  ]);

  body.push([
    {
      content:
        "NET PROFIT / (LOSS)",

      styles: {
        fontStyle:
          "bold",

        fillColor: [
          240,
          240,
          240,
        ],

        lineWidth: {
          top: 0.8,
          bottom: 0.5,
        },
      },
    },

    {
      content:
        fmt(netProfit),

      styles: {
        fontStyle:
          "bold",

        fillColor: [
          240,
          240,
          240,
        ],

        lineWidth: {
          top: 0.8,
          bottom: 0.5,
        },
      },
    },
  ]);

  body.push([
    boldCell(
      "Net Profit Margin"
    ),

    boldCell(
      fmtPct(
        netMargin
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        infoStartY + 12,

      head: [
        [
          {
            content:
              "Description",

            styles: {
              halign:
                "left",
            },
          },

          {
            content:
              "RM",

            styles: {
              halign:
                "right",
            },
          },
        ],
      ],

      body,

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      headStyles:
        HEAD_STYLE_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            130,
        },

        1: {
          halign:
            "right",

          cellWidth:
            130,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  drawNetProfitDoubleRule(
    doc,
    {
      margin,

      y:
        doc.lastAutoTable
          .finalY,
    }
  );

  const disclaimerY =
    doc.lastAutoTable
      .finalY + 26;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  const disclaimerLines =
    doc.splitTextToSize(
      "This is an internal annual management report prepared from records in the Gao Chill bookkeeping system. Supporting invoices, receipts, payment records, and bank records should be retained separately for accounting and tax purposes.",

      contentWidth
    );

  doc.text(
    disclaimerLines,
    margin,
    disclaimerY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  // ============================================================
  // SCHEDULE A — MONTHLY COMPARISON
  // ============================================================

  doc.addPage();

  let cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE A — MONTHLY COMPARISON",

        margin,

        cursorY:
          margin + 30,
      }
    );

  const compRows =
    monthlyComparison.map(
      (m) => [
        m.month,

        fmt(
          m.revenue
        ),

        fmt(
          m.productCost
        ),

        fmt(
          m.grossProfit
        ),

        fmt(
          m.eventExpenses
        ),

        fmt(
          m.companyOpex
        ),

        fmt(
          m.netProfit
        ),

        m.revenue >
        0
          ? fmtPct(
              m.netMargin
            )
          : "—",
      ]
    );

  compRows.push([
    boldCell(
      "YEAR TOTAL"
    ),

    boldCell(
      fmt(
        totalRevenue
      )
    ),

    boldCell(
      fmt(
        totalProductCost
      )
    ),

    boldCell(
      fmt(
        grossProfit
      )
    ),

    boldCell(
      fmt(
        eventExpenses.total
      )
    ),

    boldCell(
      fmt(
        companyOpex.total
      )
    ),

    boldCell(
      fmt(
        netProfit
      )
    ),

    boldCell(
      totalRevenue >
      0
        ? fmtPct(
            netMargin
          )
        : "—"
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      head: [
        [
          "Month",
          "Revenue",
          "Product Cost",
          "Gross Profit",
          "Event Expenses",
          "Company OPEX",
          "Net Profit / (Loss)",
          "Net Margin",
        ],
      ],

      body:
        compRows,

      theme:
        "grid",

      styles:
        CELL_STYLES_SCHEDULE,

      headStyles:
        HEAD_STYLE_SCHEDULE,

      columnStyles: {
        0: {
          halign:
            "left",
        },

        1: {
          halign:
            "right",
        },

        2: {
          halign:
            "right",
        },

        3: {
          halign:
            "right",
        },

        4: {
          halign:
            "right",
        },

        5: {
          halign:
            "right",
        },

        6: {
          halign:
            "right",
        },

        7: {
          halign:
            "right",
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 20;

  // ============================================================
  // SCHEDULE B — ANNUAL EXPENSE BREAKDOWN
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE B — ANNUAL EXPENSE BREAKDOWN",

        margin,

        cursorY,
      }
    );

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(10);

  doc.text(
    "Event / Activity Expenses",

    margin,

    cursorY
  );

  cursorY += 6;

  const evBody =
    eventExpenseBreakdown.map(
      (r) => [
        r.category,

        fmt(
          r.amount
        ),
      ]
    );

  evBody.push([
    boldCell(
      "Sub-total"
    ),

    boldCell(
      fmt(
        eventExpenses.total
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      head: [
        [
          "Expense Category",
          "Amount RM",
        ],
      ],

      body:
        evBody,

      theme:
        "grid",

      styles:
        CELL_STYLES_SCHEDULE,

      headStyles:
        HEAD_STYLE_SCHEDULE,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 14;

  const pageHeight =
    doc.internal.pageSize.getHeight();

  if (
    cursorY + 100 >
    pageHeight - 40
  ) {
    doc.addPage();

    cursorY =
      margin + 30;
  }

  doc.setFont(
    "helvetica",
    "bold"
  );

  doc.setFontSize(10);

  doc.text(
    "Company Operating Expenses",

    margin,

    cursorY
  );

  cursorY += 6;

  const opBody =
    annualOpexBreakdown.length >
    0
      ? annualOpexBreakdown.map(
          (r) => [
            r.category,

            fmt(
              r.amount
            ),
          ]
        )
      : [
          [
            {
              content:
                "None recorded",

              colSpan:
                2,

              styles: {
                fontStyle:
                  "italic",

                halign:
                  "left",
              },
            },
          ],
        ];

  opBody.push([
    boldCell(
      "Sub-total"
    ),

    boldCell(
      fmt(
        companyOpex.total
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      head: [
        [
          "Expense Category",
          "Amount RM",
        ],
      ],

      body:
        opBody,

      theme:
        "grid",

      styles:
        CELL_STYLES_SCHEDULE,

      headStyles:
        HEAD_STYLE_SCHEDULE,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 20;

  // ============================================================
  // SCHEDULE C — OWNER WITHDRAWALS
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE C — OWNER WITHDRAWALS AND PROFIT RETAINED",

        margin,

        cursorY,

        minRoomBelow:
          130,
      }
    );

  autoTable(
    doc,
    {
      startY:
        cursorY,

      body: [
        [
          "Annual Net Profit",

          fmt(
            netProfit
          ),
        ],

        [
          "Less: Nelson Owner Withdrawals",

          fmt(
            ownerDraws
          ),
        ],

        [
          boldTop(
            "Profit Retained in Business"
          ),

          boldTop(
            fmt(
              profitRetained
            )
          ),
        ],
      ],

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 10;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  doc.text(
    "Owner withdrawals do not reduce business net profit.",

    margin,

    cursorY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  cursorY += 22;

  // ============================================================
  // SCHEDULE D — CASH SUMMARY
  // ============================================================

  cursorY =
    startSchedule(
      doc,
      {
        title:
          "SCHEDULE D — CASH SUMMARY",

        margin,

        cursorY,

        minRoomBelow:
          180,
      }
    );

  const cashRows = [
    [
      "Opening Cash Balance",

      fmt(
        cashSummary.openingCash ||
          0
      ),
    ],

    [
      "Cash Received",

      fmt(
        cashSummary.cashReceived
      ),
    ],

    [
      "Operating Expenses Paid",

      fmt(
        cashSummary.opexPaid
      ),
    ],

    [
      "Product Purchases Paid",

      fmt(
        cashSummary.productPurchases
      ),
    ],

    [
      "Asset Purchases Paid",

      fmt(
        cashSummary.assetPurchases
      ),
    ],

    [
      "Owner Withdrawals",

      fmt(
        cashSummary.ownerWithdrawals
      ),
    ],
  ];

  if (
    (
      cashSummary.pendingInflow ||
      0
    ) !== 0
  ) {
    cashRows.push([
      "Confirmed Pending Cash Inflows",

      fmt(
        cashSummary.pendingInflow
      ),
    ]);
  }

  if (
    (
      cashSummary.pendingOutflow ||
      0
    ) !== 0
  ) {
    cashRows.push([
      "Confirmed Pending Cash Outflows",

      fmt(
        cashSummary.pendingOutflow
      ),
    ]);
  }

  cashRows.push([
    boldTop(
      "Net Cash Movement"
    ),

    boldTop(
      fmt(
        cashSummary.netCashMovement
      )
    ),
  ]);

  cashRows.push([
    boldCell(
      "Closing Cash Balance"
    ),

    boldCell(
      fmt(
        cashSummary.closingCash
      )
    ),
  ]);

  autoTable(
    doc,
    {
      startY:
        cursorY,

      body:
        cashRows,

      theme:
        "plain",

      styles:
        CELL_STYLES_MAIN,

      columnStyles: {
        0: {
          halign:
            "left",

          cellWidth:
            contentWidth -
            160,
        },

        1: {
          halign:
            "right",

          cellWidth:
            160,
        },
      },

      margin: {
        left:
          margin,

        right:
          margin,
      },
    }
  );

  cursorY =
    doc.lastAutoTable
      .finalY + 10;

  doc.setFont(
    "helvetica",
    "italic"
  );

  doc.setFontSize(8);

  doc.setTextColor(
    60,
    60,
    60
  );

  doc.text(
    "Cash Summary is separate from the Profit and Loss Statement.",

    margin,

    cursorY
  );

  doc.setTextColor(
    0,
    0,
    0
  );

  drawChromeOnAllPages(
    doc,
    {
      runningHeaderText:
        "Gao Chill — Annual Management Accounts",

      footerCenterText:
        `Annual P&L · FY ${year}`,

      margin,
    }
  );

  const filename =
    `Gao-Chill-Annual-Management-P&L-${sanitize(
      String(year)
    )}.pdf`;

  doc.save(
    filename
  );

  return filename;
};
